import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { AuthProvider } from "@/context/AuthContext";
import { UserAuthProvider } from "@/context/UserAuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { WishlistProvider } from "@/context/WishlistContext";
import WishlistPage from "@/pages/WishlistPage";
import AppReturnPage from "@/pages/AppReturnPage";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import CartPage from "@/pages/CartPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import AdminPage from "@/pages/AdminPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AlibabaImportPage from "@/pages/AlibabaImportPage";
import TemuImportPage from "@/pages/TemuImportPage";
import TrackingPage from "@/pages/TrackingPage";
import UpdatePrompt from "@/components/UpdatePrompt";
import WhatsNewModal from "@/components/WhatsNewModal";
import OnboardingWalkthrough from "@/components/OnboardingWalkthrough";
import RateAppPrompt, { useRatePrompt } from "@/components/RateAppPrompt";
import { registerServiceWorker, setUpdateCallback } from "@/serviceWorkerRegistration";

// User Auth Pages
import UserLoginPage from "@/pages/UserLoginPage";
import UserRegisterPage from "@/pages/UserRegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";

// Merchant Pages
import MerchantRegisterPage from "@/pages/MerchantRegisterPage";
import MerchantLoginPage from "@/pages/MerchantLoginPage";
import MerchantDashboardPage from "@/pages/MerchantDashboardPage";
import MerchantProductFormPage from "@/pages/MerchantProductFormPage";
import StorefrontPage from "@/pages/StorefrontPage";
import AdminMerchantsPage from "@/pages/AdminMerchantsPage";
import CommissionsPage from "@/pages/CommissionsPage";
import StockAlertsPage from "@/pages/StockAlertsPage";
import FulfillmentPage from "@/pages/FulfillmentPage";

function App() {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  useEffect(() => {
    // Register service worker and set up update callback
    setUpdateCallback(() => {
      setShowUpdatePrompt(true);
    });
    registerServiceWorker();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <UserAuthProvider>
          <CurrencyProvider>
            <CartProvider>
              <WishlistProvider>
              <div className="App">
                <BrowserRouter>
                  <AppRouter showUpdatePrompt={showUpdatePrompt} setShowUpdatePrompt={setShowUpdatePrompt} />
                </BrowserRouter>
              </div>
              </WishlistProvider>
            </CartProvider>
          </CurrencyProvider>
        </UserAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Separate router component to use useLocation hook
function AppRouter({ showUpdatePrompt, setShowUpdatePrompt }) {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.pathname.startsWith('/app-return')) {
    return <AppReturnPage />;
  }
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }
  
  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/products/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        </Route>
        
        {/* User Auth Routes */}
        <Route path="/login" element={<UserLoginPage />} />
        <Route path="/register" element={<UserRegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/callback" element={<GoogleAuthCallback />} />
        
        {/* Order Tracking (Public) */}
        <Route path="/track" element={<TrackingPage />} />
        <Route path="/track/:orderId" element={<TrackingPage />} />
        
        {/* Merchant Public Storefront */}
        <Route path="/store/:storeSlug" element={<StorefrontPage />} />
        
        {/* Merchant Auth (Public) */}
        <Route path="/merchant/register" element={<MerchantRegisterPage />} />
        <Route path="/merchant/login" element={<MerchantLoginPage />} />
        
        {/* Merchant Dashboard (Protected by token in component) */}
        <Route path="/merchant/dashboard" element={<MerchantDashboardPage />} />
        <Route path="/merchant/products/new" element={<MerchantProductFormPage />} />
        <Route path="/merchant/products/:productId/edit" element={<MerchantProductFormPage />} />
        
        {/* Admin Login (Public) */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        
        {/* Protected Admin Routes */}
        <Route element={<Layout />}>
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/merchants" element={
            <ProtectedRoute>
              <AdminMerchantsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/alibaba" element={
            <ProtectedRoute>
              <AlibabaImportPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/temu" element={
            <ProtectedRoute>
              <TemuImportPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/commissions" element={
            <ProtectedRoute>
              <CommissionsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/stock-alerts" element={
            <ProtectedRoute>
              <StockAlertsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/fulfillment" element={
            <ProtectedRoute>
              <FulfillmentPage />
            </ProtectedRoute>
          } />
        </Route>
      </Routes>
      <Toaster position="bottom-right" richColors />
      {showUpdatePrompt && (
        <UpdatePrompt onDismiss={() => setShowUpdatePrompt(false)} />
      )}
      <WhatsNewModal />
      <OnboardingWalkthrough />
    </>
  );
}

export default App;
